import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { useParams, useSearchParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useProject } from '../../../../lib/db/hooks'
import { db } from '../../../../lib/db/indexeddb'
import { scheduleSync } from '../../../../lib/sync/sync-engine'
import { useTabActions } from '../../../../contexts/TabManagerContext'
import type { Tab } from '../../../../hooks/multiWindow'
import { useBreadcrumbTitle } from '../../../../contexts/BreadcrumbTitleContext'
import { dispatchAppEvent, onAppEvent } from '../../../../lib/events'
import type { Task, Section } from '../../../../lib/db/models'
import type { TaskCreationData } from '../../components/TaskEntry'
import type { TaskListHandle } from '../../components/TaskList'
import { TaskBoard } from '../../components/TaskBoard'
import { useProjectNavigation } from '../../hooks/useProjectNavigation'
import { useTaskOperations } from '../../hooks/useTaskOperations'
import { useProjectSections } from '../../hooks/useProjectSections'
import { useProjectHeader } from '../../hooks/useProjectHeader'
import { useTaskCreation } from '../../hooks/useTaskCreation'
import { useTaskDragAndDrop } from '../../hooks/useTaskDragAndDrop'
import { useSectionDrag, type OptimisticSectionUpdates } from '../../hooks/useSectionDrag'
import { useSectionDragAnimation } from '../../hooks/useSectionDragAnimation'
import { useProjectKeyboardShortcuts } from '../../hooks/useProjectKeyboardShortcuts'
import { useDuplicateItems } from '../../hooks/useDuplicateItems'
import { useContextMenuBadgeUpdate } from '../../hooks/useContextMenuBadgeUpdate'
import { useOptimisticTaskOverrides } from '../../hooks/useOptimisticTaskOverrides'
import { useOptimisticSectionOverrides } from '../../hooks/useOptimisticSectionOverrides'
import type { OptimisticUpdates } from '../../hooks/useTaskDragAndDrop'
import { usePageCrossfade } from './hooks/usePageCrossfade'
import { useSelectionCache } from './hooks/useSelectionCache'
import { projectSelectionCache } from './hooks/projectSelectionCacheRegistry'
import { useDndComposition } from './hooks/useDndComposition'
import { ProjectSegment } from './components/ProjectSegment'
import { DragOverlayContent } from './components/DragOverlayContent'
import { useIsActiveTab } from '../../../../pages/TabStack/ActiveTabContext'
import { consumePendingTaskJump } from '../../utils/pendingTaskJump'
import '../TaskViews.scss'
import './ProjectPage.scss'

import type { RenderSegment } from './components/ProjectSegment'

interface ProjectPageProps {
  lifeSpace?: 'personal' | 'work' | 'home'
  /**
   * When rendered through the TabStack, `projectId` is passed directly from
   * the tab's path. Falls back to `useParams` for Route-based callers.
   */
  projectId?: string
  /** When rendered through TabStack, the owning tab is passed directly.
   *  Avoids subscribing to `activeTab` via context — that subscription used
   *  to re-render every ProjectPage on every tab switch. */
  tab?: Tab
}

export const ProjectPage: React.FC<ProjectPageProps> = ({ projectId: projectIdProp, lifeSpace: lifeSpaceProp, tab }) => {
  const isActive = useIsActiveTab()
  const { projectId: projectIdParam } = useParams<{ projectId: string }>()
  const projectId = projectIdProp ?? projectIdParam
  const [searchParams, setSearchParams] = useSearchParams()
  const { t } = useTranslation()
  const { data: project, loading, updateProject } = useProject(projectId ?? null)
  // Prefer the explicit tab-prop lifespace; fall back to the project's own
  // lifeSpace once loaded; otherwise 'personal' until the load resolves so
  // useTaskCreation doesn't receive `undefined`.
  const lifeSpace: 'personal' | 'work' | 'home' = lifeSpaceProp ?? project?.lifeSpace ?? 'personal'
  // `tab` prop is passed by TabContent (TabStack). We intentionally do NOT
  // subscribe to `activeTab` via context — that would re-render every
  // ProjectPage (not just the active one) on every tab switch.
  const ownTab = tab
  const { updateTab, closeActiveTab } = useTabActions()
  const { setTitle: setBreadcrumbTitle } = useBreadcrumbTitle()
  const [hoveredSegmentIndex, setHoveredSegmentIndex] = useState<number | null>(null)
  const [hoveringTasks, setHoveringTasks] = useState(false)
  const [lastInteractedSegmentIndex, setLastInteractedSegmentIndex] = useState(0)

  // Optimistic reorder: applied synchronously on drop so dnd-kit's transition
  // can animate. Cleared once useLiveQuery catches up with the Dexie write.
  // Lives here (not in useTaskDragAndDrop) because segments useMemo reads it,
  // and the hook reads segments — keeping the state here breaks the circular dependency.
  const [optimisticUpdates, setOptimisticUpdates] = useState<OptimisticUpdates>(null)
  const [optimisticSectionUpdates, setOptimisticSectionUpdates] = useState<OptimisticSectionUpdates>(null)

  // Stable ref object per segment key (for TaskList handleRef prop)
  const getSegmentHandleRef = useCallback((key: string) => {
    const map = segmentHandleRefs.current
    if (!map.has(key)) map.set(key, { current: null })
    return map.get(key)!
  }, [])

  // Batch textarea auto-resize into a single rAF to avoid per-keystroke layout thrashing.
  // Reading scrollHeight right after setting height='auto' is a forced reflow — doing it
  // inside rAF lets React paint the new text first, so the measurement is done once per frame.
  const scheduleAutoResize = useCallback((el: HTMLTextAreaElement) => {
    requestAnimationFrame(() => {
      el.style.height = 'auto'
      el.style.height = `${el.scrollHeight}px`
    })
  }, [])

  const pageRef = useRef<HTMLDivElement>(null)
  const taskListRef = useRef<HTMLDivElement>(null)
  const taskListHandleRef = useRef<TaskListHandle | null>(null)
  const segmentHandleRefs = useRef(new Map<string, { current: TaskListHandle | null }>())
  // Ref bridges: values defined after useProjectNavigation that earlier callbacks need.
  const navFocusFirstRef = useRef<(() => void) | null>(null)
  const navClearSelectionRef = useRef<(() => void) | null>(null)
  const navSelectTaskRef = useRef<((taskId: string) => void) | null>(null)
  const navSelectSectionRef = useRef<((sectionId: string) => void) | null>(null)
  const navFocusedItemRef = useRef<{ type: 'task' | 'section'; id: string } | null>(null)
  const activeSegmentRef = useRef(0)
  const isDragAnimatingRef = useRef(false)

  // ── Tab switch: data queries + save/restore selection ──
  // Seed from the module-level cache so the selection survives ProjectPage
  // unmount/remount (e.g. switching to a Quick Notes tab and back).
  const selectionCacheRef = useRef(projectSelectionCache)
  const {
    tasks, sections, pendingRestoreRef, pendingSelectionRestoreRef,
    correctDataReady,
  } = usePageCrossfade({ projectId, project })

  // ── Optimistic per-task / per-section field overrides ──
  // Let mutation handlers (toggle complete, badge updates, section
  // collapse / rename / delete, task delete / duplicate) show their effect
  // in the next paint instead of waiting on the IDB write + LiveQuery
  // refire (~1–2s when sync push/pull cycles are competing for the main
  // thread). Each hook auto-clears entries once the raw Dexie data
  // catches up — comparison runs against the RAW arrays, NOT against
  // override-applied data, otherwise cleanup would fire on the first
  // render and snap the UI back to the old value.
  const { optimisticTaskOverrides, setOptimisticTaskOverride } =
    useOptimisticTaskOverrides(tasks)
  const { optimisticSectionOverrides, setOptimisticSectionOverride } =
    useOptimisticSectionOverrides(sections)

  // ── Effective (override-applied) snapshots ──
  // Live OUTSIDE the segments memo so mutation handlers (toggle, collapse,
  // …) read the same view the user sees. Without this, two rapid clicks
  // both look at the still-stale raw Dexie data and flip in the *same*
  // direction — second click visually does nothing.
  const effectiveTasks = useMemo<Task[] | undefined>(() => {
    if (!tasks) return undefined
    // Paused tasks (ruhend) stay in the project list — TaskEntry renders
    // them with the dormant treatment (dashed-arc check, stone tint when
    // selected, three-dots reactivate trigger). They're identifiable in
    // place instead of vanishing into the Ruhend view.
    if (!optimisticUpdates && optimisticTaskOverrides.size === 0) {
      return tasks
    }
    return tasks.flatMap(t => {
      const dragUpdate = optimisticUpdates?.get(t.id)
      const fieldUpdate = optimisticTaskOverrides.get(t.id)
      if (!dragUpdate && !fieldUpdate) {
        return [t]
      }
      const merged = {
        ...t,
        ...(dragUpdate ? {
          order: dragUpdate.order,
          ...(dragUpdate.sectionId !== undefined ? { sectionId: dragUpdate.sectionId } : {}),
        } : {}),
        ...(fieldUpdate ?? {}),
      }
      if (merged.deletedAt) return [] // optimistic delete
      return [merged]
    })
  }, [tasks, optimisticUpdates, optimisticTaskOverrides])

  const effectiveSections = useMemo<Section[] | undefined>(() => {
    if (!sections) return undefined
    if (!optimisticSectionUpdates && optimisticSectionOverrides.size === 0) return sections
    return sections.flatMap(s => {
      const orderUpdate = optimisticSectionUpdates?.get(s.id)
      const fieldUpdate = optimisticSectionOverrides.get(s.id)
      if (!orderUpdate && !fieldUpdate) return [s]
      const merged = {
        ...s,
        ...(orderUpdate ? { order: orderUpdate.order } : {}),
        ...(fieldUpdate ?? {}),
      }
      if (merged.deletedAt) return [] // optimistic delete
      return [merged]
    })
  }, [sections, optimisticSectionUpdates, optimisticSectionOverrides])

  // Build render segments: interleaved sections + task groups sorted by order
  const segments = useMemo<RenderSegment[]>(() => {
    if (!effectiveTasks || !effectiveSections) {
      return [{ section: null, tasks: effectiveTasks ?? [], taskCount: effectiveTasks?.length ?? 0 }]
    }

    // Merge tasks + sections into a single sorted list
    type Item = { type: 'task'; data: Task } | { type: 'section'; data: Section }
    const items: Item[] = [
      ...effectiveTasks.map(t => ({ type: 'task' as const, data: t })),
      ...effectiveSections.map(s => ({ type: 'section' as const, data: s })),
    ]
    items.sort((a, b) => a.data.order - b.data.order)

    // Build collapsed set from EFFECTIVE sections so optimistic collapse
    // overrides flip the section view in the next paint instead of
    // waiting on the IDB → LiveQuery roundtrip.
    const collapsedIds = new Set(effectiveSections.filter(s => s.collapsed).map(s => s.id))

    // Walk items, build segments
    type MutableSegment = RenderSegment & { allTasks: Task[] }
    const result: MutableSegment[] = []
    let current: MutableSegment = { section: null, tasks: [], taskCount: 0, allTasks: [] }

    for (const item of items) {
      if (item.type === 'section') {
        // Flush current segment
        if (current.section || current.allTasks.length > 0) {
          result.push(current)
        }
        current = {
          section: item.data,
          tasks: [],
          taskCount: 0,
          collapsed: collapsedIds.has(item.data.id),
          allTasks: [],
        }
      } else {
        // Always count (even collapsed tasks). `allTasks` holds the full list
        // for mount-stable rendering; `tasks` excludes collapsed ones so
        // external consumers (nav, drag, gating) behave as before.
        current.taskCount++
        current.allTasks.push(item.data)
        if (current.section && collapsedIds.has(current.section.id)) continue
        current.tasks.push(item.data)
      }
    }
    // Flush last segment
    if (current.section || current.allTasks.length > 0) {
      result.push(current)
    }

    // Ensure at least one segment so AddTask renders in empty projects
    if (result.length === 0) {
      result.push({ section: null, tasks: [], taskCount: 0 })
    }

    return result
  }, [effectiveTasks, effectiveSections])

  // Keep taskListHandleRef in sync with the first segment's handle (for focusFirst, selectTask, etc.)
  useEffect(() => {
    if (segments.length === 0) return
    const firstKey = segments[0].section?.id
    if (firstKey) {
      const segRef = segmentHandleRefs.current.get(firstKey)
      if (segRef) taskListHandleRef.current = segRef.current
    }
  }, [segments])

  // ── Scroll to section (triggered by sidebar sub-menu click or initial load) ──
  // Uses a dedicated event instead of watching searchParams to avoid a feedback
  // loop with scroll-spy: scroll-spy updates ?section= → this effect fires →
  // scrollIntoView fights the user's ongoing scroll momentum.
  const lastScrolledSectionRef = useRef<string | null>(searchParams.get('section'))
  useEffect(() => {
    let outerTimer: ReturnType<typeof setTimeout> | null = null
    let innerTimer: ReturnType<typeof setTimeout> | null = null
    const cleanup = onAppEvent('scroll-to-section', (sectionId) => {
      if (lastScrolledSectionRef.current === sectionId) return
      lastScrolledSectionRef.current = sectionId
      isScrollSpySuppressedRef.current = true
      if (outerTimer) clearTimeout(outerTimer)
      outerTimer = setTimeout(() => {
        const el = document.querySelector<HTMLElement>(`.section-header[data-section-id="${sectionId}"]`)
        if (el) {
          el.scrollIntoView({ behavior: 'smooth', block: 'start' })
        }
        if (innerTimer) clearTimeout(innerTimer)
        innerTimer = setTimeout(() => {
          isScrollSpySuppressedRef.current = false
        }, 600)
      }, 100)
    })
    return () => {
      cleanup()
      if (outerTimer) clearTimeout(outerTimer)
      if (innerTimer) clearTimeout(innerTimer)
    }
  }, [])

  // On initial load with ?section= param, scroll to that section once data is ready
  useEffect(() => {
    if (!correctDataReady) return
    const sectionId = searchParams.get('section')
    if (!sectionId) return
    if (lastScrolledSectionRef.current === sectionId) return
    dispatchAppEvent('scroll-to-section', sectionId)
  }, [correctDataReady]) // Only on initial data ready, not on searchParams changes

  // After the "Im Projekt aufgeweckt"-toast button opens this project,
  // a one-shot stash carries the woken task's id. Consume it whenever
  // this project tab becomes active with data ready: when the tab was
  // pre-mounted via TabStack `display: none`, neither `correctDataReady`
  // nor `projectId` change on activation — only `isActive` does, so it
  // has to be in the deps for the consume to fire on tab-switch. The
  // stash itself is one-shot, so re-firing without a pending id is a
  // cheap no-op.
  useEffect(() => {
    if (!correctDataReady) return
    if (!isActive) return
    const pendingTaskId = consumePendingTaskJump(projectId)
    if (!pendingTaskId) return

    // Defer past the next paint so segments are rendered and the row's
    // DOM node has its final position.
    const timer = setTimeout(() => {
      const el = document.querySelector(`[data-task-id="${CSS.escape(pendingTaskId)}"]`)
      if (el instanceof HTMLElement) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' })
      }
      // Visible selection in ProjectPage is driven by the navigation
      // hook's `selectedIds`, NOT the per-TaskList internal state. Going
      // through `navSelectTaskRef` (the same setter Cmd/Click uses)
      // makes the row actually highlight.
      navSelectTaskRef.current?.(pendingTaskId)
    }, 150)

    return () => clearTimeout(timer)
  }, [correctDataReady, projectId, isActive])

  // ── Scroll-spy: update ?section= param as user scrolls past section headers ──
  const isScrollSpySuppressedRef = useRef(false)
  const scrollSpySectionRef = useRef<string | null>(searchParams.get('section'))

  // Listen for suppress events from sidebar (e.g. project click → scroll to top)
  useEffect(() => {
    let suppressReleaseTimer: ReturnType<typeof setTimeout> | null = null
    const cleanupSuppress = onAppEvent('scroll-spy-suppress', () => {
      isScrollSpySuppressedRef.current = true
      scrollSpySectionRef.current = null
      lastScrolledSectionRef.current = null
      if (suppressReleaseTimer) clearTimeout(suppressReleaseTimer)
      suppressReleaseTimer = setTimeout(() => {
        isScrollSpySuppressedRef.current = false
      }, 800)
    })
    // Keyboard navigation (CMD/Alt+Arrow) knows which section it landed in.
    // Update the sidebar directly so the active sub-item reflects the target.
    const cleanupSetSection = onAppEvent('scroll-spy-set-section', (sectionId) => {
      const current = scrollSpySectionRef.current
      if (sectionId && sectionId !== current) {
        scrollSpySectionRef.current = sectionId
        lastScrolledSectionRef.current = sectionId
        setSearchParams({ section: sectionId }, { replace: true })
      } else if (!sectionId && current) {
        scrollSpySectionRef.current = null
        lastScrolledSectionRef.current = null
        setSearchParams({}, { replace: true })
      }
    })
    return () => {
      cleanupSuppress()
      cleanupSetSection()
      if (suppressReleaseTimer) clearTimeout(suppressReleaseTimer)
    }
  }, [setSearchParams])

  // Keep ref in sync with external param changes (e.g. sidebar click)
  useEffect(() => {
    scrollSpySectionRef.current = searchParams.get('section')
  }, [searchParams])

  useEffect(() => {
    if (!correctDataReady || !sections || sections.length === 0) return

    const scrollContainer = document.querySelector<HTMLElement>('.main-layout__scroll-wrap')
    if (!scrollContainer) return

    // Throttle: skip if a rAF is already pending
    let rafId: number | null = null

    const handleScroll = () => {
      if (isScrollSpySuppressedRef.current) return
      if (rafId !== null) return
      rafId = requestAnimationFrame(() => {
        rafId = null
        computeActiveSection(scrollContainer)
      })
    }

    const computeActiveSection = (container: HTMLElement) => {
      const headerEls = container.querySelectorAll<HTMLElement>('.section-header[data-section-id]')
      if (headerEls.length === 0) return

      const containerRect = container.getBoundingClientRect()
      // Activation line: 1/3 from the top of the scroll container.
      // A section becomes active when its header scrolls above this line.
      // This ensures the last section can become active even when the user
      // can't scroll further (content ends before the header reaches the top).
      const activationY = containerRect.top + containerRect.height / 3

      let activeId: string | null = null
      for (const el of headerEls) {
        const rect = el.getBoundingClientRect()
        // Header top is above the activation line → this section is active
        if (rect.top < activationY) {
          activeId = el.dataset.sectionId ?? null
        }
      }

      const currentSection = scrollSpySectionRef.current
      if (activeId && activeId !== currentSection) {
        scrollSpySectionRef.current = activeId
        lastScrolledSectionRef.current = activeId
        setSearchParams({ section: activeId }, { replace: true })
      } else if (!activeId && currentSection) {
        scrollSpySectionRef.current = null
        lastScrolledSectionRef.current = null
        setSearchParams({}, { replace: true })
      }
    }

    scrollContainer.addEventListener('scroll', handleScroll, { passive: true })

    return () => {
      scrollContainer.removeEventListener('scroll', handleScroll)
      if (rafId !== null) cancelAnimationFrame(rafId)
    }
  }, [correctDataReady, sections, setSearchParams])



  // ── Project header (title, notes, breadcrumb, tab sync) ──
  const {
    name, setName, savedName,
    notesValue, setNotesValue, savedNotes,
    isEditingHeader, setIsEditingHeader,
    titleRef, notesRef, headerRef,
    handleKeyDown, handleNotesKeyDown, handleHeaderBlur,
  } = useProjectHeader({
    project, updateProject, projectId,
    activeTab: ownTab, updateTab, closeActiveTab, setBreadcrumbTitle,
    navFocusFirstRef, t,
    isActive,
  })

  // ── Task creation ──
  const {
    creatingPosition, creatingAfterTaskId, creatingSegmentIdx, pendingInlineTarget, morphData, positionMorphKey, inlineMorphTitle, inlineMorphSelected, inlineMorphKey, creationKey, inlineResetKey, skipInlineEntrance, autoSelectId,
    startCreating, startCreatingAfter,
    handleCreateTask, handleCreateTaskAndContinue,
    handleCreateTaskAfter, handleCreateTaskAfterAndContinue,
    handleCancelCreate, handleMorphComplete, handleInlineMorphComplete,
    topCreateRef, bottomCreateRef,
  } = useTaskCreation({
    projectId, lifeSpace, segments, tasks, sections,
    activeSegmentRef, navClearSelectionRef, navSelectTaskRef, navSelectSectionRef, navFocusedItemRef,
    setLastInteractedSegmentIndex,
  })

  // ── Task operations (edit, complete, delete) ──
  // Wired with the optimistic-override sink so toggle / inline-edit /
  // delete paint in the next paint instead of waiting for the IDB write
  // → LiveQuery roundtrip. We pass `effectiveTasks` (override-applied)
  // so a rapid second click reads the *intended* status — not the still-
  // stale raw Dexie row that hasn't seen the LiveQuery refire yet.
  const { handleUpdateTask, handleToggleComplete, handleDeleteTask } = useTaskOperations({
    t, setOptimisticTaskOverride, tasks: effectiveTasks,
  })

  // ── Cmd/Ctrl+Enter in expanded mode: save edit + open creator right after task ──
  const handleUpdateTaskAndCreateBelow = useCallback(async (taskId: string, data: TaskCreationData) => {
    await handleUpdateTask(taskId, data)
    startCreatingAfter(taskId)
  }, [handleUpdateTask, startCreatingAfter])

  // ── Section operations ──
  const {
    autoEditSectionId,
    handleAddSection,
    handleToggleCollapse,
    handleRenameSection,
    handleDeleteSection,
    toggleCollapseRef,
  } = useProjectSections({
    projectId, tasks: effectiveTasks, sections: effectiveSections, segments,
    setOptimisticSectionOverride, setOptimisticTaskOverride,
  })

  // ── Unified cross-section keyboard navigation ──
  const navigation = useProjectNavigation({
    segments,
    onEscapeTop: () => {
      setIsEditingHeader(true)
      requestAnimationFrame(() => {
        (notesRef.current ?? titleRef.current)?.focus()
      })
    },
    onDelete: handleDeleteTask,
    onDeleteSection: handleDeleteSection,
    onToggleComplete: handleToggleComplete,
    onToggleCollapse: (sectionId: string) => toggleCollapseRef.current?.(sectionId),
    onCreateBelow: startCreatingAfter,
    onCreateInSection: (sectionId: string) => {
      const segIdx = segments.findIndex(s => s.section?.id === sectionId)
      if (segIdx < 0) return
      activeSegmentRef.current = segIdx
      startCreating('top')
    },
    isDragAnimatingRef,
    skipFocusCorrectionRef: pendingSelectionRestoreRef,
    isActive,
  })
  // Destructure stable useCallback references for dependency arrays
  const { clearSelection: navClearSelection, selectTask: navSelectTask } = navigation
  navFocusFirstRef.current = navigation.focusFirst
  navClearSelectionRef.current = navClearSelection
  navSelectTaskRef.current = navSelectTask
  navSelectSectionRef.current = navigation.selectSection

  // ── Focus the task-list wrapper whenever this page becomes active ──
  // TaskList's arrow-key handler is bound to its own element (onKeyDown via
  // listProps) and `navigation.onKeyDown` to the wrapper. With TabStack the
  // page gets `display: none` when inactive, which drops the browser's
  // focused element onto <body>. Without this effect the user had to click
  // the list once after every tab switch to get keyboard nav back.
  useEffect(() => {
    if (!isActive) return
    // Wait one frame — on the first activation the wrapper div may not yet
    // be in the DOM (loading skeleton branch still rendering).
    const rafId = requestAnimationFrame(() => {
      // Don't steal focus if the user is already typing somewhere (e.g. in
      // the project title input, which mounts before the wrapper).
      const active = document.activeElement
      const typingTag = active?.tagName
      if (typingTag === 'INPUT' || typingTag === 'TEXTAREA') return
      navigation.wrapperRef.current?.focus({ preventScroll: true })
    })
    return () => cancelAnimationFrame(rafId)
  }, [isActive, navigation.wrapperRef])
  const focusedItem = navigation.focusedNavIndex !== null ? navigation.flatNavList[navigation.focusedNavIndex] : null
  navFocusedItemRef.current = focusedItem ? { type: focusedItem.type, id: focusedItem.id } : null

  // ── Selection save/restore across tab switches ──
  useSelectionCache({
    projectId, navigation, pendingRestoreRef, pendingSelectionRestoreRef,
    correctDataReady, selectionCacheRef,
  })

  // Derive which segment is "active" for AddTask positioning
  const activeSegmentIndex = useMemo(() => {
    // 1. Selection has priority — when a task/section is selected, anchor AddTask there
    if (navigation.focusedNavIndex !== null) {
      const focusedItem = navigation.flatNavList[navigation.focusedNavIndex]
      if (focusedItem) {
        const segIdx = segments.findIndex(seg =>
          focusedItem.type === 'section'
            ? seg.section?.id === focusedItem.id
            : seg.tasks.some(t => t.id === focusedItem.id)
        )
        if (segIdx !== -1) return segIdx
      }
    }
    // 2. No selection — hover determines position
    if (hoveredSegmentIndex !== null && hoveredSegmentIndex < segments.length) {
      return hoveredSegmentIndex
    }
    // 3. Fallback: last interacted segment
    return Math.min(lastInteractedSegmentIndex, segments.length - 1)
  }, [hoveredSegmentIndex, navigation.focusedNavIndex, navigation.flatNavList, segments, lastInteractedSegmentIndex])

  // Keep ref in sync so callbacks defined before useMemo can read the latest value
  activeSegmentRef.current = activeSegmentIndex

  // AddTask visibility: show when hovering over tasks OR when a task/section is selected
  const showAddTask = (hoveredSegmentIndex !== null && hoveringTasks) || navigation.focusedNavIndex !== null

  // Track last interacted segment from keyboard navigation
  useEffect(() => {
    if (navigation.focusedNavIndex === null) return
    const focusedItem = navigation.flatNavList[navigation.focusedNavIndex]
    if (!focusedItem) return
    const segIdx = segments.findIndex(seg =>
      focusedItem.type === 'section'
        ? seg.section?.id === focusedItem.id
        : seg.tasks.some(t => t.id === focusedItem.id)
    )
    if (segIdx !== -1) setLastInteractedSegmentIndex(segIdx)
  }, [navigation.focusedNavIndex, navigation.flatNavList, segments])

  // ── Drag-and-drop ──
  const {
    activeDragId, draggedIds, dragCount, isCrossSegmentDrag, gapAfterSegmentIdx, gapHeight,
    crossSegGapAfterRef, crossSegGapAfter,
    sensors, handleDragStart, handleDragMove, handleDragOver, handleDragEnd, handleDragCancel,
  } = useTaskDragAndDrop({
    segments, tasks, sections, taskListRef, navSelectTask, setOptimisticUpdates,
  })
  // ── Section drag-and-drop ──
  const {
    activeSectionDragId,
    hoverTargetIdx,
    dropAnimRef: sectionDropAnimRef,
    segmentRectsRef,
    sourceHeaderHeightRef,
    sourceHeaderTopRef,
    handleSectionDragStart,
    handleSectionDragOver,
    handleSectionDragEnd,
    handleSectionDragCancel,
  } = useSectionDrag({ segments, tasks, sections, wrapperRef: taskListRef, setOptimisticUpdates, setOptimisticSectionUpdates })

  // Section drag animations: counter-transforms, swap, FLIP settle
  useSectionDragAnimation({
    wrapperRef: taskListRef,
    activeSectionDragId,
    hoverTargetIdx,
    segments,
    dropAnimRef: sectionDropAnimRef,
    segmentRectsRef,
    sourceHeaderHeightRef,
  })

  // Clear optimistic section updates after section drag completes.
  // Delayed so useLiveQuery has time to pick up ALL Dexie writes (sections + tasks).
  // Section reorder can touch many items (25+ tasks), so allow more time than task drag.
  useEffect(() => {
    if (!activeSectionDragId) {
      const timer = setTimeout(() => {
        setOptimisticSectionUpdates(() => null)
        setOptimisticUpdates(() => null)
      }, 300)
      return () => clearTimeout(timer)
    }
  }, [activeSectionDragId])

  isDragAnimatingRef.current = activeDragId !== null || activeSectionDragId !== null

  // Dynamic overlay Y offset: measure how much the source header shifted after CSS changes.
  // Replaces the fixed SECTION_DRAG_OVERLAY_OFFSET for section drags.
  const [sectionDragYOffset, setSectionDragYOffset] = useState(0)
  useLayoutEffect(() => {
    if (!activeSectionDragId) {
      setSectionDragYOffset(0)
      return
    }
    const headerEl = document.querySelector<HTMLElement>(`.section-header[data-section-id="${activeSectionDragId}"]`)
    if (headerEl) {
      const postCssTop = headerEl.getBoundingClientRect().top
      setSectionDragYOffset(postCssTop - sourceHeaderTopRef.current)
    }
  }, [activeSectionDragId])

  // ── Composed DnD orchestration (section + task drag) ──
  const excludeId = (morphData || inlineMorphTitle) ? autoSelectId ?? undefined : undefined
  const {
    composedDragStart, composedDragMove, composedDragOver, composedDragEnd, composedDragCancel,
    customCollisionDetection, allSortableIds, segmentAwareStrategy,
    sectionDragWidth, dragRotation, dragSourceSegIdx,
  } = useDndComposition({
    segments, excludeId,
    handleSectionDragStart, handleSectionDragOver, handleSectionDragEnd, handleSectionDragCancel,
    activeSectionDragId,
    handleDragStart, handleDragMove, handleDragOver, handleDragEnd, handleDragCancel,
    crossSegGapAfterRef, crossSegGapAfter,
  })

  const handleHoverLeave = useCallback(() => {
    // Don't reset hover state while an AddTask dropdown is open —
    // the dropdown is a Radix portal outside the hover zone.
    if (document.querySelector('.add-task--open')) return
    setHoveredSegmentIndex(null)
    setHoveringTasks(false)
  }, [])

  // ── Duplicate items (Cmd/Ctrl+D) ──
  // Pass `effectiveTasks` (override-merged) so that a duplicate fired
  // immediately after a status-toggle clones the toggled state — not the
  // stale Dexie row that's still mid-LiveQuery-refire.
  const { handleDuplicate } = useDuplicateItems({
    projectId, segments, tasks: effectiveTasks, sections,
    selectedIds: navigation.selectedIds,
    flatNavList: navigation.flatNavList,
    focusedNavIndex: navigation.focusedNavIndex,
    setSelectedIds: navigation.setSelectedIds,
    setFocusedNavIndexWithAnchor: navigation.setFocusedNavIndexWithAnchor,
  })

  // Right-click "Duplicate": if the clicked task is part of a multi-selection,
  // duplicate all selected tasks (matches Delete behaviour). Otherwise duplicate
  // just the clicked task, bypassing the selection state entirely to avoid
  // timing issues with async selection updates from handleContextMenu.
  const handleDuplicateFromContextMenu = useCallback((taskId: string) => {
    const selected = navigation.selectedIds
    if (selected.size > 1 && selected.has(taskId)) {
      handleDuplicate()
    } else {
      handleDuplicate([taskId])
    }
  }, [handleDuplicate, navigation.selectedIds])

  const handleContextMenuBadgeUpdate = useContextMenuBadgeUpdate({
    getSelection: () => navigation.selectedIds,
    setOptimisticTaskOverride,
    logTag: 'ProjectPage',
  })

  // ── Keyboard shortcuts & IPC listeners ──
  useProjectKeyboardShortcuts({
    savedName, tasks, creatingPosition, creatingAfterTaskId, segments,
    activeSegmentRef, navClearSelection, navSelectTask,
    startCreating, startCreatingAfter, handleAddSection, handleDuplicate,
    focusedNavIndex: navigation.focusedNavIndex,
    flatNavList: navigation.flatNavList,
    isActive,
  })

  // While the project loads (or if it truly doesn't exist — Dexie returns
  // undefined for both), render the empty shell. The title input's `name`
  // state starts as '' so the placeholder takes over until data arrives,
  // preventing the skeleton-to-title position jump that was visible on
  // Quick Notes → Project tab switches.
  if (loading || !project) {
    return (
      <div className="task-view project-page">
        <div className="task-view__header">
          <div className="task-view__title-section" />
        </div>
      </div>
    )
  }

  return (
    <div ref={pageRef} className="task-view project-page">
      <div className="task-view__header">
        <div
          ref={headerRef}
          className="task-view__title-section"
          onBlur={handleHeaderBlur}
        >
          <textarea
            ref={titleRef}
            className="project-page__title-input"
            value={name}
            placeholder={t('project.defaultName')}
            onChange={(e) => {
              setName(e.target.value)
              scheduleAutoResize(e.currentTarget)
            }}
            onKeyDown={handleKeyDown}
            onFocus={() => setIsEditingHeader(true)}
            rows={1}
          />
          {/* Notes: textarea for multiline, auto-resizes to content.
              Hidden only when empty and not editing (takes no space). */}
          {(isEditingHeader || savedNotes) && (
            <textarea
              ref={notesRef}
              className="project-page__notes-input"
              value={notesValue}
              placeholder={isEditingHeader ? t('project.notesPlaceholder') : undefined}
              onChange={(e) => {
                setNotesValue(e.target.value)
                scheduleAutoResize(e.currentTarget)
              }}
              onKeyDown={handleNotesKeyDown}
              onFocus={() => setIsEditingHeader(true)}
              rows={1}
            />
          )}
        </div>
      </div>

      <TaskBoard
        inProjectContext
        segments={segments}
        tasks={tasks}
        projectId={projectId}
        savedName={savedName}
        gapAfterSegmentIdx={gapAfterSegmentIdx}
        gapHeight={gapHeight}
        activeDragId={activeDragId}
        draggedIds={draggedIds}
        dragCount={dragCount ?? 0}
        isCrossSegmentDrag={isCrossSegmentDrag}
        activeSectionDragId={activeSectionDragId}
        dragSourceSegIdx={dragSourceSegIdx}
        sectionDragWidth={sectionDragWidth}
        dragRotation={dragRotation}
        sectionDragYOffset={sectionDragYOffset}
        taskListRef={taskListRef}
        taskListHandleRef={taskListHandleRef}
        getSegmentHandleRef={getSegmentHandleRef}
        sensors={sensors}
        customCollisionDetection={customCollisionDetection}
        allSortableIds={allSortableIds}
        segmentAwareStrategy={segmentAwareStrategy}
        composedDragStart={composedDragStart}
        composedDragMove={composedDragMove}
        composedDragOver={composedDragOver}
        composedDragEnd={composedDragEnd}
        composedDragCancel={composedDragCancel}
        navigation={navigation}
        hoveredSegmentIndex={hoveredSegmentIndex}
        setHoveredSegmentIndex={setHoveredSegmentIndex}
        hoveringTasks={hoveringTasks}
        setHoveringTasks={setHoveringTasks}
        activeSegmentIndex={activeSegmentIndex}
        showAddTask={showAddTask}
        creatingSegmentIdx={creatingSegmentIdx}
        creatingPosition={creatingPosition}
        creatingAfterTaskId={creatingAfterTaskId}
        pendingInlineTarget={pendingInlineTarget}
        creationKey={creationKey}
        inlineResetKey={inlineResetKey}
        skipInlineEntrance={skipInlineEntrance}
        autoSelectId={autoSelectId}
        morphData={morphData}
        positionMorphKey={positionMorphKey}
        inlineMorphTitle={inlineMorphTitle}
        inlineMorphSelected={inlineMorphSelected}
        inlineMorphKey={inlineMorphKey}
        topCreateRef={topCreateRef}
        bottomCreateRef={bottomCreateRef}
        startCreating={startCreating}
        handleCreateTask={handleCreateTask}
        handleCreateTaskAndContinue={handleCreateTaskAndContinue}
        handleCreateTaskAfter={handleCreateTaskAfter}
        handleCreateTaskAfterAndContinue={handleCreateTaskAfterAndContinue}
        handleCancelCreate={handleCancelCreate}
        handleMorphComplete={handleMorphComplete}
        handleInlineMorphComplete={handleInlineMorphComplete}
        autoEditSectionId={autoEditSectionId}
        handleToggleCollapse={handleToggleCollapse}
        handleRenameSection={handleRenameSection}
        handleDeleteSection={handleDeleteSection}
        handleAddSection={handleAddSection}
        handleToggleComplete={handleToggleComplete}
        handleDeleteTask={handleDeleteTask}
        handleUpdateTask={handleUpdateTask}
        handleUpdateTaskAndCreateBelow={handleUpdateTaskAndCreateBelow}
        handleDuplicate={handleDuplicateFromContextMenu}
        handleContextMenuBadgeUpdate={handleContextMenuBadgeUpdate}
        optimisticUpdates={optimisticUpdates}
        handleHoverLeave={handleHoverLeave}
        handleClearSelection={navClearSelection}
      />
    </div>
  )
}
